/**
 * Chunk load error handler for handling 404s on missing assets
 * Provides a reload mechanism when old tabs encounter missing assets
 */

// @ts-expect-error - i18n.js doesn't have TypeScript declarations
import i18n from '../i18n.js';

class ChunkErrorHandler {
  private reloadPromptShown = false;

  constructor() {
    // ChunkErrorHandler initialized
  }

  /**
   * Show a reload prompt to the user
   */
  showReloadPrompt(): void {
    if (this.reloadPromptShown) {
      return;
    }

    this.reloadPromptShown = true;
    console.log('🔄 [ChunkErrorHandler] Showing reload prompt');

    // Create a non-intrusive banner
    const banner = document.createElement('div');
    banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #007bff;
      color: white;
      padding: 12px 20px;
      text-align: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      z-index: 10000;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
    `;

    const message = document.createElement('span');
    message.textContent = i18n.t('alert.APP_UPDATED');

    const reloadButton = document.createElement('button');
    reloadButton.textContent = i18n.t('actions.refresh');
    reloadButton.style.cssText = `
      background: rgba(255,255,255,0.2);
      border: 1px solid rgba(255,255,255,0.3);
      color: white;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 13px;
      transition: background-color 0.2s;
    `;

    reloadButton.addEventListener('mouseenter', () => {
      reloadButton.style.backgroundColor = 'rgba(255,255,255,0.3)';
    });

    reloadButton.addEventListener('mouseleave', () => {
      reloadButton.style.backgroundColor = 'rgba(255,255,255,0.2)';
    });

    reloadButton.addEventListener('click', () => {
      this.reloadApp();
    });

    const dismissButton = document.createElement('button');
    dismissButton.textContent = i18n.t('actions.dismiss');
    dismissButton.style.cssText = `
      background: none;
      border: none;
      color: white;
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background-color 0.2s;
    `;

    dismissButton.addEventListener('mouseenter', () => {
      dismissButton.style.backgroundColor = 'rgba(255,255,255,0.2)';
    });

    dismissButton.addEventListener('mouseleave', () => {
      dismissButton.style.backgroundColor = 'transparent';
    });

    dismissButton.addEventListener('click', () => {
      banner.remove();
      this.reloadPromptShown = false;
    });

    banner.appendChild(message);
    banner.appendChild(reloadButton);
    banner.appendChild(dismissButton);

    // Add some top margin to body to account for the banner
    document.body.style.marginTop = '48px';
    document.body.appendChild(banner);

    // Auto-dismiss after 30 seconds
    setTimeout(() => {
      if (banner.parentNode) {
        banner.remove();
        document.body.style.marginTop = '';
        this.reloadPromptShown = false;
      }
    }, 30000);
  }

  /**
   * Reload the app
   */
  reloadApp(): void {
    console.log('🔄 [ChunkErrorHandler] Reloading app...');
    window.location.reload();
  }

  /**
   * Handle chunk load errors (404s on missing assets)
   */
  handleChunkLoadError(error: Error): void {
    console.warn('⚠️ [ChunkErrorHandler] Chunk load error detected:', error);

    // Check if this is a chunk load error
    const errorMessage = error.message || error.toString();
    const isChunkError = /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(errorMessage);

    if (isChunkError && !this.reloadPromptShown) {
      console.log('🔄 [ChunkErrorHandler] Chunk load error - showing reload prompt');
      this.showReloadPrompt();
    }
  }
}

// Create singleton instance
export const chunkErrorHandler = new ChunkErrorHandler();

// Global error handlers for chunk load errors
if (typeof window !== 'undefined') {
  // Handle unhandled promise rejections (common for chunk load errors)
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof Error) {
      chunkErrorHandler.handleChunkLoadError(event.reason);
    } else if (typeof event.reason === 'string') {
      const error = new Error(event.reason);
      chunkErrorHandler.handleChunkLoadError(error);
    }
  });

  // Handle general errors
  window.addEventListener('error', (event) => {
    if (event.error instanceof Error) {
      chunkErrorHandler.handleChunkLoadError(event.error);
    }
  });
}

export default chunkErrorHandler;
